import React, { createContext, useContext, useState, useCallback } from 'react';
import config from '../config';
import { useAuth } from './AuthContext';

// Course interface matching design document
interface Course {
  id: string;
  title: string;
  description: string;
  instructor: string;
  thumbnail: string;
  type: 'Live' | 'Self-Paced';
  status: 'Pending' | 'In Progress' | 'Completed';
  progress: number;
  dueDate?: string; // ISO 8601
  enrolledAt: string; // ISO 8601
}

// Course state interface
interface CourseState {
  enrolledCourses: Course[];
  currentCourse: Course | null;
  recommendations: Course[];
  isLoading: boolean;
  error: string | null;
}

// Course context value interface
interface CourseContextValue extends CourseState {
  fetchEnrolledCourses: () => Promise<void>;
  fetchCourseDetails: (courseId: string) => Promise<void>;
  enrollInCourse: (courseId: string) => Promise<void>;
  searchCourses: (query: string) => Promise<Course[]>;
  filterCourses: (type: 'All' | 'Live' | 'Self-Paced') => Course[];
  clearError: () => void;
}

// Create context with undefined default
const CourseContext = createContext<CourseContextValue | undefined>(undefined);

// Custom hook to use course context
export const useCourse = () => {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
};

// CourseProvider component
interface CourseProviderProps {
  children: React.ReactNode;
}

export const CourseProvider: React.FC<CourseProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<CourseState>({
    enrolledCourses: [],
    currentCourse: null,
    recommendations: [],
    isLoading: false,
    error: null,
  });

  // Get token from localStorage
  const getToken = useCallback(() => {
    return localStorage.getItem('authToken');
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Fetch enrolled courses
  const fetchEnrolledCourses = useCallback(async () => {
    if (!isAuthenticated) {
      setState(prev => ({
        ...prev,
        enrolledCourses: [],
        error: 'User not authenticated',
      }));
      return;
    }

    const token = getToken();
    if (!token) {
      setState(prev => ({
        ...prev,
        error: 'No authentication token found',
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${config.backendUrl}/courses/enrolled`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success && data.courses) {
        setState(prev => ({
          ...prev,
          enrolledCourses: data.courses,
          recommendations: data.recommendations || [],
          isLoading: false,
        }));
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: data.message || 'Failed to fetch enrolled courses',
        }));
      }
    } catch (error) {
      console.error('Fetch enrolled courses error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to fetch enrolled courses. Please try again.',
      }));
    }
  }, [isAuthenticated, getToken]);

  // Fetch course details
  const fetchCourseDetails = useCallback(async (courseId: string) => {
    if (!isAuthenticated) {
      setState(prev => ({
        ...prev,
        error: 'User not authenticated',
      }));
      return;
    }

    const token = getToken();
    if (!token) {
      setState(prev => ({
        ...prev,
        error: 'No authentication token found',
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${config.backendUrl}/courses/${courseId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success && data.course) {
        setState(prev => ({
          ...prev,
          currentCourse: data.course,
          isLoading: false,
        }));
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: data.message || 'Failed to fetch course details',
        }));
      }
    } catch (error) {
      console.error('Fetch course details error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to fetch course details. Please try again.',
      }));
    }
  }, [isAuthenticated, getToken]);

  // Enroll in a course
  const enrollInCourse = useCallback(async (courseId: string) => {
    if (!isAuthenticated) {
      setState(prev => ({
        ...prev,
        error: 'User not authenticated',
      }));
      return;
    }

    const token = getToken();
    if (!token) {
      setState(prev => ({
        ...prev,
        error: 'No authentication token found',
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${config.backendUrl}/courses/${courseId}/enroll`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        // Refresh enrolled courses after successful enrollment
        await fetchEnrolledCourses();
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: data.message || 'Failed to enroll in course',
        }));
      }
    } catch (error) {
      console.error('Enroll in course error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to enroll in course. Please try again.',
      }));
    }
  }, [isAuthenticated, getToken, fetchEnrolledCourses]);

  // Search courses
  const searchCourses = useCallback(async (query: string): Promise<Course[]> => {
    if (!isAuthenticated) {
      console.error('User not authenticated');
      return [];
    }

    const token = getToken();
    if (!token) {
      console.error('No authentication token found');
      return [];
    }

    try {
      const response = await fetch(
        `${config.backendUrl}/courses/search?q=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success && data.courses) {
        return data.courses;
      } else {
        console.error('Search failed:', data.message);
        return [];
      }
    } catch (error) {
      console.error('Search courses error:', error);
      return [];
    }
  }, [isAuthenticated, getToken]);

  // Filter courses by type
  const filterCourses = useCallback((type: 'All' | 'Live' | 'Self-Paced'): Course[] => {
    if (type === 'All') {
      return state.enrolledCourses;
    }
    return state.enrolledCourses.filter(course => course.type === type);
  }, [state.enrolledCourses]);

  const value: CourseContextValue = {
    ...state,
    fetchEnrolledCourses,
    fetchCourseDetails,
    enrollInCourse,
    searchCourses,
    filterCourses,
    clearError,
  };

  return <CourseContext.Provider value={value}>{children}</CourseContext.Provider>;
};

export default CourseContext;
